import React, { useContext, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  MapPin,
  BookOpen,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import toast from "react-hot-toast";
import { AuthContext } from "../../contexts/AuthContext";
import { getMyStudentProfile } from "../../services/students";

type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

const DAYS_OF_WEEK: DayOfWeek[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_NAMES: Record<DayOfWeek, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

const DAY_COLORS: Record<DayOfWeek, string> = {
  monday: "bg-blue-100 text-blue-800 border-blue-200",
  tuesday: "bg-purple-100 text-purple-800 border-purple-200",
  wednesday: "bg-emerald-100 text-emerald-800 border-emerald-200",
  thursday: "bg-amber-100 text-amber-800 border-amber-200",
  friday: "bg-pink-100 text-pink-800 border-pink-200",
  saturday: "bg-cyan-100 text-cyan-800 border-cyan-200",
  sunday: "bg-slate-100 text-slate-700 border-slate-200",
};

const StudentSchedule: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const [selectedDay, setSelectedDay] = useState<DayOfWeek | "all">("all");

  // Fetch the student's own profile — the backend pre-filters periods to only
  // those assigned to this student (via studentIds field on each period).
  const {
    data: studentData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["student-schedule"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id,
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-blue-500"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const errMsg =
      (error as any)?.response?.data?.message ||
      (error as any)?.message ||
      "Unable to load your schedule";
    toast.error(errMsg);
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 flex items-center justify-center">
        <div className="bg-white/95 backdrop-blur-xl border border-red-200/60 rounded-2xl p-6 shadow-lg max-w-md">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-red-300 to-pink-400 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-6 w-6 text-white" />
          </div>
          <h3 className="text-slate-900 font-bold text-center text-lg">
            Error Loading Schedule
          </h3>
          <p className="text-slate-600 font-medium mt-2 text-center">
            {errMsg}
          </p>
        </div>
      </div>
    );
  }

  const student = studentData?.data;

  // Prevent e-course students from accessing regular student pages
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center max-w-md">
          <div className="mx-auto w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mb-4">
            <CheckCircle className="w-8 h-8 text-orange-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Access Restricted
          </h2>
          <p className="text-gray-600 mb-4">
            Class schedule is not available for e-course students.
          </p>
          <a
            href="/student/ecourse-dashboard"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to E-Learning Dashboard
          </a>
        </div>
      </div>
    );
  }

  // Periods pre-filtered by the backend — only periods where this student's
  // ID is in studentIds (or studentIds is empty meaning all students).
  const periods: any[] = student?.schedule?.periods || [];
  const courses = student?.coursesInfo || [];

  const todayName = new Date()
    .toLocaleDateString("en-US", { weekday: "long" })
    .toLowerCase() as DayOfWeek;

  const todayPeriods = periods
    .filter((p: any) => p.day?.toLowerCase() === todayName)
    .sort((a: any, b: any) => a.startTime.localeCompare(b.startTime));

  // Days that actually have periods assigned to this student
  const activeDays = DAYS_OF_WEEK.filter((day) =>
    periods.some((p: any) => p.day?.toLowerCase() === day),
  );

  const daysToShow =
    selectedDay === "all"
      ? activeDays
      : activeDays.filter((d) => d === selectedDay);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-lg border border-slate-200/60 p-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">
            My Schedule
          </h1>
          <p className="text-slate-500 mt-1 text-sm font-medium">
            Your personal class timetable
          </p>
        </div>

        {/* Today's Classes */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 lg:p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            Today's Classes
            <span className="text-slate-400 font-medium text-sm">
              {DAY_NAMES[todayName] ?? todayName}
            </span>
          </h2>

          {todayPeriods.length > 0 ? (
            <div className="space-y-2">
              {todayPeriods.map((period: any, i: number) => (
                <div
                  key={i}
                  className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200/60 rounded-xl"
                >
                  <div className="flex items-center gap-1.5 bg-blue-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold shrink-0">
                    <Clock className="h-3.5 w-3.5" />
                    {period.startTime} – {period.endTime}
                  </div>
                  <div className="min-w-0">
                    <p className="font-bold text-slate-900 text-sm truncate">
                      {period.subjectName}
                    </p>
                    {period.room && (
                      <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                        <MapPin className="h-3 w-3" />
                        {period.room}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <CheckCircle className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
              <p className="text-slate-500 font-medium text-sm">
                No classes today — enjoy your day!
              </p>
            </div>
          )}
        </div>

        {/* Weekly Schedule List */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-sm">
          {/* Filter bar */}
          <div className="p-4 border-b border-slate-200/60 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-bold text-slate-900">
              Weekly Schedule
            </h2>
            <select
              value={selectedDay}
              onChange={(e) =>
                setSelectedDay(e.target.value as DayOfWeek | "all")
              }
              className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm font-medium bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Days</option>
              {DAYS_OF_WEEK.map((day) => (
                <option key={day} value={day}>
                  {DAY_NAMES[day]}
                </option>
              ))}
            </select>
          </div>

          <div className="p-4 space-y-6">
            {periods.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="font-bold text-slate-700 mb-1">
                  No schedule assigned
                </p>
                <p className="text-slate-400 text-sm">
                  Your timetable will appear here once it has been set up.
                </p>
              </div>
            ) : daysToShow.length === 0 ? (
              <div className="text-center py-10">
                <p className="text-slate-500 font-medium text-sm">
                  No classes on {DAY_NAMES[selectedDay as DayOfWeek]}.
                </p>
              </div>
            ) : (
              daysToShow.map((day) => {
                const dayPeriods = periods
                  .filter((p: any) => p.day?.toLowerCase() === day)
                  .sort((a: any, b: any) =>
                    a.startTime.localeCompare(b.startTime),
                  );
                return (
                  <div key={day}>
                    {/* Day heading */}
                    <div className="flex items-center gap-2 mb-3">
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-bold border ${DAY_COLORS[day]} ${day === todayName ? "ring-2 ring-blue-400 ring-offset-1" : ""}`}
                      >
                        {DAY_NAMES[day]}
                        {day === todayName && (
                          <span className="ml-1.5 opacity-70">· Today</span>
                        )}
                      </span>
                      <div className="flex-1 h-px bg-slate-100" />
                    </div>

                    {/* Period rows */}
                    <div className="space-y-2 pl-1">
                      {dayPeriods.map((period: any, i: number) => (
                        <div
                          key={i}
                          className="flex items-center gap-3 p-3 border border-slate-200/70 rounded-xl bg-white hover:shadow-sm transition-shadow"
                        >
                          <div className="flex items-center gap-1.5 text-slate-500 text-xs font-semibold w-28 shrink-0">
                            <Clock className="h-3.5 w-3.5 shrink-0" />
                            {period.startTime} – {period.endTime}
                          </div>
                          <div className="w-px h-8 bg-slate-200 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-bold text-slate-900 text-sm truncate">
                              {period.subjectName}
                            </p>
                            {period.room && (
                              <p className="text-xs text-slate-400 flex items-center gap-1 mt-0.5">
                                <MapPin className="h-3 w-3 shrink-0" />
                                {period.room}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Enrolled Courses */}
        {courses.length > 0 && (
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-md">
                <BookOpen className="h-5 w-5 text-white" />
              </div>
              Enrolled Courses
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {(Array.isArray(courses) ? courses : [courses]).map(
                (course: any) => (
                  <div
                    key={course._id}
                    className="p-4 bg-gradient-to-br from-slate-50 to-indigo-50 border border-slate-200 rounded-xl"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-slate-900 text-sm truncate mr-2">
                        {course.name}
                      </h3>
                      {course.level && (
                        <span className="text-xs font-semibold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full shrink-0">
                          {course.level}
                        </span>
                      )}
                    </div>
                    {course.description && (
                      <p className="text-xs text-slate-500 line-clamp-2">
                        {course.description}
                      </p>
                    )}
                  </div>
                ),
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StudentSchedule;
